import React, { useState, useEffect, useRef } from 'react';
import { X } from 'lucide-react';
import api from '../services/apiClient';
import './ExchangeConfigModal.css';

function InfraConfigModal({ infraType, infraData, onClose, onSave }) {
  const [formData, setFormData] = useState({});
  const [isSaving, setIsSaving] = useState(false);
  const [error, setError] = useState(null);
  const initializedRef = useRef(false);

  // Initialize form based on infra type
  useEffect(() => {
    if (!initializedRef.current && infraData) {
      const initialData = {};
      
      if (infraType === 'rpc') {
        initialData.arbitrumRpcUrl = '';
        initialData.ethereumRpcUrl = '';
      } else if (infraType === 'etherscan') {
        initialData.apiKey = '';
      }
      
      setFormData(initialData);
      initializedRef.current = true;
    }
  }, [infraType, infraData]);

  const handleChange = (key, value) => {
    setFormData(prev => ({ ...prev, [key]: value }));
  };

  const handleSave = async () => {
    setIsSaving(true);
    setError(null);

    try {
      if (infraType === 'rpc') {
        // Save RPC URLs
        if (formData.arbitrumRpcUrl?.trim()) {
          await api.put('/admin/secrets/ARBITRUM_RPC_URL', { value: formData.arbitrumRpcUrl.trim() });
        }
        if (formData.ethereumRpcUrl?.trim()) {
          await api.put('/admin/secrets/ETHEREUM_RPC_URL', { value: formData.ethereumRpcUrl.trim() });
        }
      } else if (infraType === 'etherscan') {
        // Save Etherscan API key
        if (formData.apiKey?.trim()) {
          await api.put('/admin/secrets/ETHERSCAN_API_KEY', { value: formData.apiKey.trim() });
        }
      }

      onSave();
      onClose();
    } catch (err) {
      setError(err.response?.data?.error || err.message || 'Failed to save');
    } finally {
      setIsSaving(false);
    }
  };

  const getTitle = () => {
    switch (infraType) {
      case 'rpc': return 'RPC Settings';
      case 'etherscan': return 'Etherscan API Key';
      default: return 'Infra Settings';
    }
  };

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="modal-content exchange-modal" onClick={(e) => e.stopPropagation()}>
        <div className="modal-header">
          <h2>{getTitle()}</h2>
          <button className="btn-close-modal" onClick={onClose}>
            <X size={20} />
          </button>
        </div>

        <div className="modal-body">
          {error && (
            <div className="error-message" style={{ marginBottom: '16px', padding: '12px', background: '#fee', color: '#c33', borderRadius: '4px' }}>
              {error}
            </div>
          )}

          {infraType === 'rpc' && (
            <>
              <div className="form-group">
                <label>Arbitrum RPC URL</label>
                <input
                  type="text"
                  className="form-input"
                  placeholder={infraData?.arbitrumRpcUrlPreview ? `Current: ${infraData.arbitrumRpcUrlPreview}` : "https://arb1.arbitrum.io/rpc"}
                  value={formData.arbitrumRpcUrl || ''}
                  onChange={(e) => handleChange('arbitrumRpcUrl', e.target.value)}
                />
              </div>

              <div className="form-group">
                <label>Ethereum RPC URL</label>
                <input
                  type="text"
                  className="form-input"
                  placeholder={infraData?.ethereumRpcUrlPreview ? `Current: ${infraData.ethereumRpcUrlPreview}` : "https://eth.llamarpc.com"}
                  value={formData.ethereumRpcUrl || ''}
                  onChange={(e) => handleChange('ethereumRpcUrl', e.target.value)}
                />
              </div>
            </>
          )}

          {infraType === 'etherscan' && (
            <div className="form-group">
              <label>Etherscan API Key</label>
              <input
                type="password"
                className="form-input"
                placeholder="Your Etherscan API key (also used for Arbiscan)"
                value={formData.apiKey || ''}
                onChange={(e) => handleChange('apiKey', e.target.value)}
              />
              {infraData?.apiKeyPreview && (
                <small style={{ color: '#888', marginTop: '4px', display: 'block' }}>
                  Current: {infraData.apiKeyPreview}
                </small>
              )}
            </div>
          )}
        </div>

        <div className="modal-footer">
          <button className="btn-secondary" onClick={onClose}>
            Cancel
          </button>
          <button className="btn-primary" onClick={handleSave} disabled={isSaving}>
            {isSaving ? 'Saving...' : 'Save'}
          </button>
        </div>
      </div>
    </div>
  );
}

export default InfraConfigModal;
